import * as React from 'react';

import useInterval from '@/hooks/use-interval';
import useWindowDimensions from '@/hooks/use-window-dimensions';
import type { ConfettiStatus } from '@/helpers/newsletter.helpers';

import ConfettiGeyser from '@/components/ConfettiGeyser';
import InPortal from '@/components/InPortal';

type Mode = 'burst' | 'stream' | 'preload';

interface Props {
  status: ConfettiStatus;
  mode: Mode;
}

// Subscribe Geysers have two modes:
// - Burst: A big burst of confetti that slowly tapers to 0
// - Stream: A constant stream of moderate confetti
function SubscribeGeyser({ status, mode = 'burst' }: Props) {
  const windowDimensions = useWindowDimensions();

  const [burstConcentration, setBurstConcentration] =
    React.useState(100);

  // In portrait orientations (mostly mobile), we want to fire straight up from the bottom of the screen.
  const isPortrait =
    typeof windowDimensions.width === 'number' &&
    typeof windowDimensions.height === 'number' &&
    windowDimensions.width < windowDimensions.height;

  const geyserPosition: [number, number] = React.useMemo(() => {
    if (
      typeof windowDimensions.width !== 'number' ||
      typeof windowDimensions.height !== 'number'
    ) {
      // Should never actually be used:
      return [0, 1000];
    }

    if (isPortrait) {
      return [windowDimensions.width / 2, windowDimensions.height];
    } else {
      return [0, Math.min(700, windowDimensions.height)];
    }
  }, [isPortrait, windowDimensions.width, windowDimensions.height]);

  const intervalDuration =
    burstConcentration > 0 && status === 'active' && mode === 'burst'
      ? 250
      : null;

  useInterval(() => {
    let newConcentration = burstConcentration * 0.7 - 1;

    if (newConcentration < 1) {
      newConcentration = 0;
    }

    setBurstConcentration(newConcentration);
  }, intervalDuration);

  let concentration;
  if (status === 'inert') {
    // The `ConfettiGeyser` component is lazy-loaded, meaning that it’s only downloaded when it’s rendered. When `status` is "inert", we won’t render it.
    return null;
  } else if (status === 'preload') {
    // When the user submits the form, the status switches to "preload", and we’ll render the ConfettiGeyser, but with 0 concentration (so no visible confetti yet).
    concentration = 0;
  } else {
    // We know at this point that the confetti is running, so we’ll set the confetti based on the mode. In burst, it starts high and falls to 0. In stream, it stays constant.
    concentration = mode === 'burst' ? burstConcentration : 16;
  }

  return (
    <InPortal>
      <ConfettiGeyser
        position={geyserPosition}
        enableCollisions={false}
        airFriction={0.04}
        velocity={30}
        angularVelocity={0.6}
        angle={isPortrait ? -90 : -45}
        spread={5}
        volatility={0.6}
        concentration={concentration}
      />
    </InPortal>
  );
}

export default SubscribeGeyser;
